import {
  BgColorsOutlined,
  CalendarOutlined,
  CarOutlined,
  DashboardOutlined,
  DollarOutlined,
  FieldNumberOutlined,
  FireOutlined,
  KeyOutlined,
  SafetyCertificateOutlined,
  SettingOutlined,
  SlidersOutlined,
  ToolOutlined,
  TrophyOutlined,
  UserOutlined,
} from '@ant-design/icons';
import { Vehicle } from '@services';
import { Descriptions } from 'antd';
import dayjs from 'dayjs';
import React from 'react';

const labelStyle = { display: 'flex', alignItems: 'center', gap: 6 };
const emptyStyle: React.CSSProperties = { color: 'rgba(0, 0, 0, 0.45)', fontSize: 14 };

// Field icons are rendered in a neutral grey rather than per-field colours.
const ICON_GREY = '#8c8c8c';

const Label = ({ icon, text }: { icon: React.ReactNode; text: string }) => (
  <span style={labelStyle}>
    {React.isValidElement(icon)
      ? React.cloneElement(icon as React.ReactElement<{ style?: React.CSSProperties }>, {
          style: { color: ICON_GREY },
        })
      : icon}{' '}
    {text}
  </span>
);

// A missing value is a dash, with the wordy explanation on hover: spelled out, the "No ... provided"
// strings were the widest thing in every grid.
const Value = ({ children, fallback }: { children?: React.ReactNode; fallback: string }) =>
  children ? (
    <>{children}</>
  ) : (
    <span style={emptyStyle} title={fallback}>
      —
    </span>
  );

const formatMotExpiry = (date?: Date) => {
  if (!date || !dayjs(date).isValid() || dayjs(date).isSame(dayjs(0))) return 'Exempt from MOT';
  return dayjs(date).format('DD/MM/YYYY');
};

interface VehicleDescriptionsProps {
  vehicle: Vehicle;
  column?: number | Partial<Record<'xs' | 'sm' | 'md' | 'lg' | 'xl' | 'xxl', number>>;
  fixedWidth?: boolean;
  /** Width of the label cells, so values line up across rows (fixedWidth implies 140 plus a content width). */
  labelWidth?: number;
}

export const VehicleDescriptions: React.FC<VehicleDescriptionsProps> = ({
  vehicle,
  column = { xs: 1, sm: 2, md: 3 },
  fixedWidth = false,
  labelWidth,
}) => {
  const resolvedLabelWidth = labelWidth ?? (fixedWidth ? 140 : undefined);
  return (
    <Descriptions
      size="small"
      column={column}
      bordered
      styles={{
        label: {
          fontWeight: 'bold',
          whiteSpace: 'nowrap',
          ...(resolvedLabelWidth && { width: resolvedLabelWidth }),
        },
        content: fixedWidth ? { width: 200 } : undefined,
      }}
    >
      <Descriptions.Item label={<Label icon={<CarOutlined style={{ color: '#1677ff' }} />} text="Make" />}>
        <Value fallback="No make provided">{vehicle.combinedMake}</Value>
      </Descriptions.Item>
      <Descriptions.Item label={<Label icon={<CarOutlined style={{ color: '#722ed1' }} />} text="Model" />}>
        <Value fallback="No model provided">{vehicle.combinedModel}</Value>
      </Descriptions.Item>
      <Descriptions.Item label={<Label icon={<KeyOutlined style={{ color: '#fa8c16' }} />} text="VIN" />}>
        <Value fallback="No VIN provided">{vehicle.combinedVin}</Value>
      </Descriptions.Item>
      <Descriptions.Item label={<Label icon={<CalendarOutlined style={{ color: '#52c41a' }} />} text="Year" />}>
        <Value fallback="No year provided">{vehicle.dvlaYearOfManufacture}</Value>
      </Descriptions.Item>
      <Descriptions.Item label={<Label icon={<DashboardOutlined style={{ color: '#eb2f96' }} />} text="Engine size" />}>
        <Value fallback="No engine size provided">{vehicle.combinedEngineCapacity}</Value>
      </Descriptions.Item>
      <Descriptions.Item label={<Label icon={<SafetyCertificateOutlined style={{ color: '#f5222d' }} />} text="MOT expiry" />}>
        {formatMotExpiry(vehicle.motExpiryDate)}
      </Descriptions.Item>
      <Descriptions.Item label={<Label icon={<SettingOutlined style={{ color: '#13c2c2' }} />} text="Gear type" />}>
        <Value fallback="No gear type provided">{vehicle.combinedTransmission}</Value>
      </Descriptions.Item>
      <Descriptions.Item label={<Label icon={<SlidersOutlined style={{ color: '#2f54eb' }} />} text="Vehicle type" />}>
        <Value fallback="No vehicle type provided">{vehicle.vehicleCategoryDescription}</Value>
      </Descriptions.Item>
      <Descriptions.Item label={<Label icon={<TrophyOutlined style={{ color: '#389e0d' }} />} text="Euro status" />}>
        <Value fallback="No euro status provided">{vehicle.euroStatus}</Value>
      </Descriptions.Item>
      <Descriptions.Item label={<Label icon={<FieldNumberOutlined style={{ color: '#722ed1' }} />} text="Model series" />}>
        <Value fallback="No model series provided">{vehicle.modelSeries}</Value>
      </Descriptions.Item>
      <Descriptions.Item label={<Label icon={<CalendarOutlined style={{ color: '#fa541c' }} />} text="First registration" />}>
        <Value fallback="No registration date provided">{vehicle.dateFirstRegistered && dayjs(vehicle.dateFirstRegistered).format('DD/MM/YYYY')}</Value>
      </Descriptions.Item>
      <Descriptions.Item label={<Label icon={<BgColorsOutlined style={{ color: '#1677ff' }} />} text="Colour" />}>
        <Value fallback="No colour provided">{vehicle.colour}</Value>
      </Descriptions.Item>
      <Descriptions.Item label={<Label icon={<CarOutlined style={{ color: '#8c8c8c' }} />} text="Wheelbase" />}>
        <Value fallback="No wheelbase provided">{vehicle.wheelplan}</Value>
      </Descriptions.Item>
      <Descriptions.Item label={<Label icon={<ToolOutlined style={{ color: '#fa8c16' }} />} text="Drive type" />}>
        <Value fallback="No drive type provided">{vehicle.driveType}</Value>
      </Descriptions.Item>
      <Descriptions.Item label={<Label icon={<SettingOutlined style={{ color: '#595959' }} />} text="Engine number" />}>
        <Value fallback="No engine number provided">{vehicle.engineNumber}</Value>
      </Descriptions.Item>
      <Descriptions.Item label={<Label icon={<DashboardOutlined style={{ color: '#8c8c8c' }} />} text="Gross weight" />}>
        <Value fallback="No gross weight provided">{vehicle.grossWeightKg}</Value>
      </Descriptions.Item>
      <Descriptions.Item label={<Label icon={<UserOutlined style={{ color: '#52c41a' }} />} text="Current keeper" />}>
        <Value fallback="No keeper date provided">{vehicle.startDateOfCurrentKeeper && dayjs(vehicle.startDateOfCurrentKeeper).format('DD/MM/YYYY')}</Value>
      </Descriptions.Item>
      <Descriptions.Item label={<Label icon={<FireOutlined style={{ color: '#f5222d' }} />} text="Fuel type" />}>
        <Value fallback="No fuel type provided">{vehicle.combinedFuelType}</Value>
      </Descriptions.Item>
      <Descriptions.Item label={<Label icon={<DollarOutlined style={{ color: '#52c41a' }} />} text="Road tax" />}>
        <Value fallback="No road tax status provided">{vehicle.taxStatus}</Value>
      </Descriptions.Item>
      <Descriptions.Item label={<Label icon={<CalendarOutlined style={{ color: '#faad14' }} />} text="Tax due" />}>
        <Value fallback="No tax due date provided">{vehicle.taxDueDate && dayjs(vehicle.taxDueDate).format('DD/MM/YYYY')}</Value>
      </Descriptions.Item>
    </Descriptions>
  );
};
